// src/pages/owner/OwnerLogin.jsx
import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Card, CardContent, TextField, Typography, Alert,
  IconButton, Tooltip, Stack, CssBaseline
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import http from '../../services/http';
import OwnerBrandFooter from '../../components/owner/OwnerBrandFooter';

const OWNER_THEME_KEY = 'owner_theme_mode';

// Host guard (Mar 2026): If this page is opened on the CMFM host, redirect to CMFM login.
const DEFAULT_TENANT_SLUG = 'aiimsjodhpur-cmfm';

function readLastTenantSlug() {
  try {
    const cand = [
      localStorage.getItem('activeTenantSlug'),
      localStorage.getItem('tenantSlug'),
      sessionStorage.getItem('activeTenantSlug'),
      sessionStorage.getItem('tenantSlug'),
    ].filter(Boolean);
    const hit = cand.map((s) => String(s || '').trim()).find(Boolean);
    return hit || '';
  } catch {
    return '';
  }
}

function isOwnerHost(hostname) {
  const h = String(hostname || '').toLowerCase();
  return h === 'owner.mededtek.in' || h.startsWith('owner.');
}

function isCmfmHost(hostname) {
  const h = String(hostname || '').toLowerCase();
  return h === 'cmfm.mededtek.in' || h.startsWith('cmfm.');
}

function getInitialOwnerMode() {
  try {
    const saved = localStorage.getItem(OWNER_THEME_KEY);
    if (saved === 'dark' || saved === 'light') return saved;
  } catch {}
  if (typeof window !== 'undefined' && window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
  return 'light';
}

export default function OwnerLogin() {
  const nav = useNavigate();

  // If someone opens the Owner login on the CMFM host, send them to CMFM login instead.
  useEffect(() => {
    try {
      const { hostname, pathname } = window.location || {};
      const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
      if (isLocal) return;

      if (!isOwnerHost(hostname) && isCmfmHost(hostname) && String(pathname || '').startsWith('/owner')) {
        const slug = readLastTenantSlug() || DEFAULT_TENANT_SLUG;
        nav(`/t/${encodeURIComponent(slug)}/cmfm/login`, { replace: true });
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const [ownerMode, setOwnerMode] = useState(getInitialOwnerMode());
  useEffect(() => { try { localStorage.setItem(OWNER_THEME_KEY, ownerMode); } catch {} }, [ownerMode]);
  const theme = useMemo(() => createTheme({ palette: { mode: ownerMode } }), [ownerMode]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');
  const [hasToken, setHasToken] = useState(!!localStorage.getItem('ownerToken'));

  const toggleOwnerMode = () => setOwnerMode((m) => (m === 'dark' ? 'light' : 'dark'));
  const doLogout = () => { try { localStorage.removeItem('ownerToken'); } catch {} setHasToken(false); };

  async function handleSubmit(e) {
    e.preventDefault();
    setErr(''); setSubmitting(true);
    try {
      const { data } = await http.post('/api/owner/login', { email, password });
      if (!data?.success || !data?.token) throw new Error(data?.message || 'Login failed');
      try { localStorage.setItem('ownerToken', data.token); } catch {}
      setHasToken(true);
      nav('/owner', { replace: true });
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.error || e?.message || 'Login failed';
      setErr(msg);
    } finally { setSubmitting(false); }
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          px: 2,
          py: { xs: 4, md: 7 },
          bgcolor: 'background.default',
          color: 'text.primary',
          minHeight: '100vh',
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 760, bgcolor: 'background.paper' }} variant="outlined">
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="overline" color="primary.main" sx={{ fontWeight: 800, letterSpacing: 1.4 }}>
                MedEdTek
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.25 }}>
                Institutional Administration
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1.25, maxWidth: 660, lineHeight: 1.7 }}>
                A digital platform for medical schools, universities, teaching hospitals, and healthcare training institutions.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 680, lineHeight: 1.7 }}>
                MedEdTek supports medical education, competency and skills tracking, supervised learning activities,
                clinical workflow documentation, dashboards, reporting, and citizen-connected services.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 680, lineHeight: 1.7 }}>
                Modules can be configured for institutional curricula, local policies, training programmes,
                clinical workflows, and service-delivery requirements.
              </Typography>
            </Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>Login</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                {hasToken && (
                  <>
                    <Button size="small" variant="outlined" onClick={() => nav('/owner')}>Home</Button>
                    <Button size="small" variant="outlined" onClick={() => nav('/owner/subjects')}>Subjects</Button>
                    <Button size="small" variant="outlined" onClick={() => nav('/owner/colleges')}>Colleges</Button>
                    <Button size="small" color="error" onClick={doLogout}>Logout</Button>
                  </>
                )}
                <Tooltip title={ownerMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
                  <IconButton onClick={toggleOwnerMode} size="small" aria-label="toggle-owner-dark-mode">
                    {ownerMode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>

            {err ? <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert> : null}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField label="Email" type="email" fullWidth margin="normal"
                         value={email} onChange={(e) => setEmail(e.target.value)} autoFocus required />
              <TextField label="Password" type="password" fullWidth margin="normal"
                         value={password} onChange={(e) => setPassword(e.target.value)} required />
              <Button type="submit" variant="contained" fullWidth disabled={submitting} sx={{ mt: 2 }}>
                {submitting ? 'Signing in…' : 'Sign in'}
              </Button>
            </Box>
            <OwnerBrandFooter compact />
          </CardContent>
        </Card>
      </Box>
    </ThemeProvider>
  );
}
